import { createFileRoute } from "@tanstack/react-router";
import {
  type ChangeEvent,
  type DragEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import WaveSurfer from "wavesurfer.js";
import { z } from "zod";

import { Attachment } from "@/components/ui/attachment-new";
import { Button } from "@/components/ui/button/button";
import {
  type LiveCaptionStatus,
  type MediaUploadJob,
  type ResumeRecord,
  useMediaUpload,
  useVoiceCapture,
} from "@/features/shared/media-upload";
import { maskSearchFragment } from "@/features/shared/route-masking";
import { formatBytes } from "@/libs/file-helper";

// `maskSearchFragment` is spread so cross-route masked-dialog openers stay safe
// on this route (see .claude/rules/webapp-routing.md).
const searchSchema = z.object({ ...maskSearchFragment });

export const Route = createFileRoute("/(dev)/media-v3-upload")({
  component: MediaV3UploadPage,
  validateSearch: searchSchema,
});

const PHASE_TONE: Record<MediaUploadJob["phase"], string> = {
  queued: "bg-gray-100 text-gray-600",
  init: "bg-blue-100 text-blue-700",
  uploading: "bg-blue-100 text-blue-700",
  finalizing: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700",
  done: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

function MediaV3UploadPage() {
  const { jobs, upload, reset, resumeRecords, resumeFile } = useMediaUpload();
  const [dragging, setDragging] = useState(false);
  const pickerRef = useRef<HTMLInputElement>(null);
  const resumeRef = useRef<HTMLInputElement>(null);

  const onFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    upload(Array.from(event.target.files ?? []));
    event.target.value = "";
  };

  const onResumePicked = (event: ChangeEvent<HTMLInputElement>) => {
    for (const file of Array.from(event.target.files ?? [])) resumeFile(file);
    event.target.value = "";
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    upload(Array.from(event.dataTransfer.files));
  };

  return (
    <main className="mx-auto max-w-3xl px-4 pt-6 pb-24 text-gray-900">
      <h1 className="text-xl font-semibold">Media v3 — upload</h1>
      <p className="mt-1 text-sm text-gray-500">
        init → PUT parts (10 MB each, retried with backoff) → final → poll{" "}
        <code className="rounded bg-gray-100 px-1">/api/v3/uploads/status</code>
        . Every processed file is standardized and AI-analyzed automatically.
        Progress is saved locally, so an interrupted upload resumes when you
        re-pick the same file. Exercises the reusable{" "}
        <code className="rounded bg-gray-100 px-1">useMediaUpload()</code> hook.
      </p>

      <VoiceTranscriptDemo />

      <ResumeSection
        records={resumeRecords}
        onPick={() => resumeRef.current?.click()}
      />
      <input
        ref={resumeRef}
        type="file"
        hidden
        multiple
        onChange={onResumePicked}
      />

      <section className="mt-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Upload
        </h2>
        <div
          className={`mt-2 rounded-xl border-2 border-dashed p-8 text-center ${
            dragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <p className="mb-3 text-sm text-gray-500">Drop files here</p>
          <button
            type="button"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            onClick={() => pickerRef.current?.click()}
          >
            Choose files
          </button>
          <input
            ref={pickerRef}
            type="file"
            hidden
            multiple
            onChange={onFilesPicked}
          />
        </div>
        {jobs.length > 0 && (
          <button
            type="button"
            className="mt-2 text-sm text-blue-600 hover:underline"
            onClick={reset}
          >
            Clear results
          </button>
        )}
      </section>

      <section className="mt-4 flex flex-col gap-3">
        {jobs.map((job) => (
          <JobCard key={job.id} job={job} />
        ))}
      </section>
    </main>
  );
}

/**
 * Record voice with LIVE captions → upload via media-v3 → the live transcript
 * is stored as the draft the moment /init assigns the attachmentId → the relay
 * pushes `transcript.ready` (the authoritative full-file Gemini pass) which
 * replaces the draft. Exercises the reusable `useVoiceCapture()` hook.
 */
function VoiceTranscriptDemo() {
  const capture = useVoiceCapture();
  const {
    phase,
    liveStatus,
    liveError,
    live,
    final,
    draftStored,
    attachmentId,
    recording,
    elapsedS,
    error,
    job,
  } = capture;
  const busy = phase === "uploading" || phase === "transcribing";
  const liveText = [live.committed, live.pending].filter(Boolean).join(" ");

  return (
    <section className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
          Voice → live captions → stored transcript
        </h2>
        <LiveCaptionBadge status={liveStatus} />
      </div>
      {liveError && (
        <p className="mt-1 font-mono text-[11px] text-amber-700">
          live leg: {liveError}
        </p>
      )}
      <p className="mt-1 text-xs text-gray-500">
        While you talk, the mic stream goes straight to the speech-to-text
        vendor with a 60 s token minted by media v3 (
        <code className="rounded bg-white px-1">/uploads/stt-grant</code>) —
        captions appear live. On stop the file uploads as usual, the live text
        is stored as the draft (
        <code className="rounded bg-white px-1">/uploads/transcript</code>), and
        the full-file Gemini transcript replaces it over{" "}
        <code className="rounded bg-white px-1">realtime.birdwatch.com</code>.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {phase === "recording" ? (
          <Button
            kind="solid"
            variant="negative"
            onClick={() => void capture.stop()}
          >
            ● Stop ({formatElapsed(elapsedS)})
          </Button>
        ) : (
          <Button
            kind="solid"
            variant="primary"
            disabled={busy}
            onClick={() => void capture.start()}
          >
            {busy ? "Working…" : "Start recording"}
          </Button>
        )}
        {phase !== "idle" && phase !== "recording" && (
          <Button kind="outline" variant="neutral" onClick={capture.reset}>
            New recording
          </Button>
        )}
        {job && (
          <span className="text-xs text-gray-600">
            {job.fileName} · {job.phase}
            {job.status ? ` · ${job.status}` : ""}
          </span>
        )}
        {attachmentId && (
          <span className="font-mono text-[11px] text-gray-400">
            channel: {attachmentId}
          </span>
        )}
      </div>

      {(phase === "recording" || (liveText && !final)) && (
        <div className="mt-3 rounded-md border border-indigo-100 bg-white px-3 py-2 text-sm text-gray-800">
          <span className="mb-1 block text-xs uppercase tracking-wide text-gray-400">
            live captions
          </span>
          {liveText ? (
            <>
              {live.committed}
              {live.pending && (
                <span className="text-gray-400"> {live.pending}</span>
              )}
            </>
          ) : (
            <span className="text-gray-400">
              {liveStatus === "unavailable"
                ? "Live captions unavailable — the transcript will still arrive after processing."
                : "Listening…"}
            </span>
          )}
        </div>
      )}

      {recording && <RecordingPlayback file={recording} />}

      {phase === "transcribing" && (
        <p className="mt-3 text-xs text-gray-500">
          {draftStored === true
            ? "Live transcript stored as draft · "
            : draftStored === false
              ? "Draft not stored · "
              : ""}
          waiting for the full-file transcript… (
          {job?.phase === "done"
            ? "processing finished"
            : (job?.phase ?? "uploading")}
          )
        </p>
      )}

      {error && (
        <p className="mt-3 rounded-md bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
          {error}
        </p>
      )}
      {final && (
        <blockquote className="mt-3 whitespace-pre-wrap rounded-md border-l-4 border-indigo-500 bg-white px-3 py-2 text-sm text-gray-800">
          <span className="mb-1 block text-xs uppercase tracking-wide text-gray-400">
            transcript{final.model ? ` · ${final.model}` : ""}
            {draftStored ? " · replaced the live draft" : ""}
          </span>
          {final.text}
        </blockquote>
      )}
    </section>
  );
}

const LIVE_BADGE: Record<LiveCaptionStatus, { label: string; tone: string }> = {
  off: { label: "live captions off", tone: "text-gray-400" },
  connecting: { label: "connecting…", tone: "text-amber-600" },
  live: { label: "● live", tone: "text-green-600" },
  unavailable: { label: "live captions unavailable", tone: "text-amber-600" },
};

function LiveCaptionBadge({ status }: { status: LiveCaptionStatus }) {
  const { label, tone } = LIVE_BADGE[status];
  return <span className={`text-xs ${tone}`}>{label}</span>;
}

function formatElapsed(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/** Playback waveform for the finished recording (same WaveSurfer pattern as media-upload-demo). */
function RecordingPlayback({ file }: { file: File }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WaveSurfer | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const url = URL.createObjectURL(file);
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "#818cf8",
      progressColor: "#4f46e5",
      height: 48,
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      url,
    });
    wsRef.current = ws;
    return () => {
      ws.destroy();
      wsRef.current = null;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  return (
    <div className="mt-3 flex items-center gap-3 rounded-md border border-indigo-100 bg-white px-3 py-2">
      <Button
        kind="soft"
        variant="primary"
        size="sm"
        onClick={() => void wsRef.current?.playPause()}
      >
        Play / pause
      </Button>
      <div ref={containerRef} className="min-w-0 flex-1" />
      <span className="shrink-0 text-xs text-gray-500">
        {formatBytes(file.size)}
      </span>
    </div>
  );
}

function JobCard({ job }: { job: MediaUploadJob }) {
  const percent =
    job.parts === 0 ? 0 : Math.round((job.partsDone / job.parts) * 100);
  const previewUri = job.thumbnailUri ?? job.contentUri;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{job.fileName}</p>
          <p className="text-xs text-gray-500">
            {formatBytes(job.size)} · {job.parts} part
            {job.parts === 1 ? "" : "s"}
            {job.contentType ? ` · ${job.contentType}` : ""}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs ${PHASE_TONE[job.phase]}`}
        >
          {job.phase}
          {job.status && job.status !== job.phase.toUpperCase()
            ? ` · ${job.status}`
            : ""}
        </span>
      </div>

      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full bg-blue-600 transition-all duration-200"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {job.partsDone} / {job.parts} parts ({percent}%)
        {job.retries > 0
          ? ` · ${job.retries} retr${job.retries === 1 ? "y" : "ies"}`
          : ""}
        {job.resumedSkipped > 0
          ? ` · resumed, skipped ${job.resumedSkipped}`
          : ""}
        {job.aiAnalyzed ? " · AI-analyzed" : ""}
      </p>

      {job.note && (
        <p className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
          {job.note}
        </p>
      )}
      {job.error && (
        <p className="mt-2 rounded-md bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
          {job.error}
        </p>
      )}

      {job.phase === "done" && previewUri && (
        <div className="mt-3 flex items-center gap-3">
          <Attachment
            fileName={job.fileName}
            originalContentUri={job.contentUri}
            thumbnailUri={job.thumbnailUri}
            contentType={job.contentType}
            className="w-24 shrink-0"
            previewClassName="rounded-xl"
          />
          {job.contentUri && (
            <a
              href={job.contentUri}
              target="_blank"
              rel="noreferrer"
              className="break-all text-xs text-blue-600 hover:underline"
            >
              Open file
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function ResumeSection({
  records,
  onPick,
}: {
  records: ResumeRecord[];
  onPick: () => void;
}) {
  if (records.length === 0) return null;
  return (
    <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Resume interrupted uploads
        </h2>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
          onClick={onPick}
        >
          Re-pick files to resume
        </button>
      </div>
      <ul className="mt-3 flex flex-col gap-2">
        {records.map((record) => {
          const total = record.init.parts.length;
          const done = record.doneParts.length;
          return (
            <li
              key={record.fingerprint}
              className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm"
            >
              <span className="truncate">{record.init.fileName}</span>
              <span className="shrink-0 text-xs text-gray-500">
                {done} / {total} parts uploaded
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
